import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ContactSupport from '@mui/icons-material/ContactSupport';
import { useTranslation } from 'react-i18next';
import OptimusButton from '../../../common/library/Button/Button';
import { TestIds } from '../../../assets/testIds';

type UpgradeSuccessModalProps = {
  open: boolean;
  onClose: () => void;
  onBookOnboardingCall: () => void;
};

export function UpgradeSuccessModal({
  open,
  onClose,
  onBookOnboardingCall,
}: UpgradeSuccessModalProps) {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testid={TestIds.successModal.dialog}
    >
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: theme.spacing(2),
          }}
        >
          <Typography
            variant="headingSm"
            color={theme.palette.grey[900]}
            data-testid={TestIds.successModal.title}
          >
            {t('memberships.successModal.title')}
          </Typography>
          <IconButton
            onClick={onClose}
            aria-label="close"
            sx={{ color: theme.palette.grey[700] }}
            data-testid={TestIds.successModal.closeButton}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 0 }}>
          {/* Description */}
          <Typography
            variant="bodyMd"
            color={theme.palette.grey[700]}
            sx={{ mb: theme.spacing(3) }}
          >
            {t('memberships.successModal.description')}
          </Typography>

          {/* What Happens Next */}
          <Box
            sx={{
              backgroundColor: theme.palette.grey[50],
              borderRadius: theme.spacing(1),
              p: theme.spacing(2.5),
              mb: theme.spacing(3),
            }}
          >
            <Typography
              variant="labelMd"
              color={theme.palette.grey[900]}
              sx={{ mb: theme.spacing(2) }}
            >
              {t('memberships.successModal.whatHappensNext')}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1.5) }}>
              {[
                t('memberships.successModal.step1'),
                t('memberships.successModal.step2'),
              ].map((stepText) => (
                <Box key={stepText} sx={{ display: 'flex', alignItems: 'flex-start', gap: theme.spacing(1) }}>
                  <CheckCircleOutlineIcon
                    sx={{
                      color: theme.palette.brand.primary,
                      fontSize: theme.spacing(2.5),
                      mt: theme.spacing(0.25),
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="bodyMd" color={theme.palette.grey[700]}>
                    {stepText}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* CTA Button */}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <OptimusButton
              buttonText={t('memberships.successModal.bookOnboardingCall') || 'Book Your Onboarding Call'}
              onClick={onBookOnboardingCall}
              data-testid={TestIds.successModal.bookOnboardingButton}
              customVariant="primary"
              size="m"
              fullWidth
              leadingIcon={ContactSupport}
            />
          </Box>
        </DialogContent>
      </Box>
    </Dialog>
  );
}
